import { useState, useMemo } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Active,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { createPortal } from "react-dom";
import { SortableOverlay } from "@/lib/Components/Sortable/SortableOverlay";
import { Drawer, DrawerOverlay, DrawerContent } from "@/components/ui/drawer";
import { Button } from "@/components/catalyst/button";
import { Description, Field, Label } from "@/components/catalyst/fieldset";
import { Select } from "@/components/catalyst/select";
import { Switch, SwitchField } from "@/components/catalyst/switch";
import { Input } from "@/components/catalyst/input";
import { AdminSection } from "@/lib/Components/Common/AdminSection";
import {
  getQueryKey,
  useGqlMutation,
  useOrgId,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  importSettingRecord,
  updateImportSettingsMutation,
} from "@/app/Import/GraphQL/updateImportSettingsMutation";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import {
  defaultColOrder,
  defaultHiddenCols,
  type ImportColumnId,
} from "@/app/Import/Utils/defaultColumnOrder";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import {
  Bars3Icon,
  Cog6ToothIcon,
  MinusIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import {
  ViewColumnsIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import type { GridState } from "ag-grid-community";

type ColumnItem = {
  id: ImportColumnId;
  visible: boolean;
};

const COLUMN_LABELS: Record<ImportColumnId, string> = {
  quantity: "columns.quantity",
  line: "columns.line",
  vehicle_code: "columns.vehicle_code",
  departure_office_code: "columns.departure_office_code",
  delivery_office_code: "columns.delivery_office_code",
  available_from: "columns.available_from",
  available_to: "columns.available_to",
  hire_units_allowed: "columns.hire_units_allowed",
  minimum_hire_units: "columns.hire_units_allowed",
  extra_hire_units_allowed: "columns.extra_hire_units_allowed",
  distance_allowed: "columns.distance_allowed",
  fuel_amount: "columns.fuel_amount",
  fuel_note: "columns.fuel_note",
  ferry_note: "columns.ferry_note",
  inclusion_note: "columns.inclusion_note",
  hire_unit_rate: "columns.hire_unit_rate",
  extra_hire_unit_supplier_net_rate:
    "columns.extra_hire_unit_supplier_net_rate",
  preparation_charge_amount: "columns.preparation_charge_amount",
  preparation_charge_note: "columns.preparation_charge_amount",
};

type ColumnMappingDrawerProps = {
  supplierId: string;
};

export function ColumnMappingDrawer({ supplierId }: ColumnMappingDrawerProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button outline onClick={() => setOpen(true)}>
        <Cog6ToothIcon className="mr-2 inline h-5 w-5" />
        Import Settings
      </Button>
      <Drawer open={open} onClose={() => setOpen(false)}>
        <DrawerOverlay />
        <DrawerContent onClose={() => setOpen(false)}>
          <ColumnMappingContent
            supplierId={supplierId}
            onClose={() => setOpen(false)}
          />
        </DrawerContent>
      </Drawer>
    </>
  );
}

function ColumnMappingContent({
  supplierId,
  onClose,
}: {
  supplierId: string;
  onClose: () => void;
}) {
  const t = useTranslations("import");
  const queryClient = useQueryClient();
  const orgId = useOrgId();

  const { data: supplierRes } = useSuspenseGqlQuery(supplierRecordQuery, {
    id: supplierId,
  });
  const supplier = supplierRes.supplier;

  const { data: importSettingRes } = useSuspenseGqlQuery(importSettingRecord, {
    supplierId,
  });

  const gridState = importSettingRes?.importSetting?.grid_state as
    | GridState
    | undefined;

  const savedOrder = (gridState?.columnOrder?.orderedColIds ??
    defaultColOrder) as ImportColumnId[];
  const savedHidden = (gridState?.columnVisibility?.hiddenColIds ??
    defaultHiddenCols) as ImportColumnId[];

  const [columns, setColumns] = useState<ColumnItem[]>(() =>
    savedOrder.map((id) => ({
      id,
      visible: !savedHidden.includes(id),
    })),
  );

  const [settings, setSettings] = useState({
    date_format: importSettingRes.importSetting?.date_format ?? "DD/MM/YYYY",
    ignore_header_row:
      importSettingRes.importSetting?.ignore_header_row ?? false,
    ignore_missing_line:
      importSettingRes.importSetting?.ignore_missing_line ?? false,
    ignore_columns: importSettingRes.importSetting?.ignore_columns ?? "",
    merge_duplicates: importSettingRes.importSetting?.merge_duplicates ?? false,
    ignore_fuel_if_has_ferry:
      importSettingRes.importSetting?.ignore_fuel_if_has_ferry ?? false,
  });

  const { mutate, isPending } = useGqlMutation(updateImportSettingsMutation, {
    onSuccess: () => {
      toast.success("Import settings saved");
      queryClient.invalidateQueries({
        queryKey: getQueryKey(importSettingRecord, { supplierId }, orgId),
      });
    },
    onError: (error) => {
      toast.error("Failed to save import settings", {
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    },
  });

  const handleSave = () => {
    const orderedColIds = columns.map((c) => c.id);
    const hiddenColIds = columns.filter((c) => !c.visible).map((c) => c.id);

    const updatedGridState: Partial<GridState> = {
      ...gridState,
      columnOrder: { orderedColIds },
      columnVisibility: { hiddenColIds },
    };

    mutate({
      input: {
        supplier_id: supplierId,
        grid_state: updatedGridState,
        ...settings,
      },
    });
  };

  const handleToggleVisibility = (id: ImportColumnId) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === id ? { ...col, visible: !col.visible } : col,
      ),
    );
  };

  const visibleColumns = columns.filter((c) => c.visible);
  const hiddenColumns = columns.filter((c) => !c.visible);

  const getLabel = (id: ImportColumnId): string => {
    const dynamicLabels: Partial<Record<ImportColumnId, string>> = {
      hire_units_allowed: `relocation_days_label.${supplier.hire_unit_type}`,
      minimum_hire_units: `minimum_hire_label.${supplier.hire_unit_type}`,
      extra_hire_units_allowed: `extra_days_label.${supplier.hire_unit_type}`,
      extra_hire_unit_supplier_net_rate: `extra_hire_unit_net_rate_label.${supplier.hire_unit_type}`,
      distance_allowed: `mileage_label.${supplier.measurement}`,
    };
    const key = dynamicLabels[id] ?? COLUMN_LABELS[id];
    if (!key) return id;
    try {
      return t(key as any);
    } catch {
      return id;
    }
  };

  const [activeTab, setActiveTab] = useState("columns");

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Import Settings
            </h2>
            <p className="text-sm text-gray-500">
              Configure column mapping and import options.
            </p>
          </div>
          <Button plain onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="px-6">
          <ImportSettingsTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <ImportSettingsTabContent
          activeTab={activeTab}
          visibleColumns={visibleColumns}
          hiddenColumns={hiddenColumns}
          columns={columns}
          setColumns={setColumns}
          handleToggleVisibility={handleToggleVisibility}
          getLabel={getLabel}
          settings={settings}
          setSettings={setSettings}
          onSave={handleSave}
          isSaving={isPending}
        />
      </div>
    </div>
  );
}

function ImportSettingsTabs({
  activeTab,
  setActiveTab,
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}) {
  const tabs = [
    {
      id: "columns",
      name: "Columns",
      Icon: ViewColumnsIcon,
    },
    {
      id: "settings",
      name: "Settings",
      Icon: AdjustmentsHorizontalIcon,
    },
  ];

  return (
    <div className="flex gap-1">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`group relative flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-all duration-200 ease-in-out ${
            activeTab === tab.id
              ? "border-indigo-500 text-indigo-600"
              : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
          }`}
        >
          <tab.Icon
            className={`h-5 w-5 flex-shrink-0 transition-colors duration-200 ${
              activeTab === tab.id
                ? "text-indigo-600"
                : "text-gray-400 group-hover:text-gray-500"
            }`}
          />
          <span>{tab.name}</span>
        </button>
      ))}
    </div>
  );
}

type ImportSettingsState = {
  date_format: string;
  ignore_header_row: boolean;
  ignore_missing_line: boolean;
  ignore_columns: string;
  merge_duplicates: boolean;
  ignore_fuel_if_has_ferry: boolean;
};

function ImportSettingsTabContent({
  activeTab,
  visibleColumns,
  hiddenColumns,
  columns,
  setColumns,
  handleToggleVisibility,
  getLabel,
  settings,
  setSettings,
  onSave,
  isSaving,
}: {
  activeTab: string;
  visibleColumns: ColumnItem[];
  hiddenColumns: ColumnItem[];
  columns: ColumnItem[];
  setColumns: (columns: ColumnItem[]) => void;
  handleToggleVisibility: (id: ImportColumnId) => void;
  getLabel: (id: ImportColumnId) => string;
  settings: ImportSettingsState;
  setSettings: React.Dispatch<React.SetStateAction<ImportSettingsState>>;
  onSave: () => void;
  isSaving: boolean;
}) {
  switch (activeTab) {
    case "columns":
      return (
        <div className="space-y-6">
          <div>
            <h3 className="mb-1 text-sm font-medium text-gray-900">
              Visible Columns
            </h3>
            <p className="mb-3 text-xs text-gray-500">
              These columns map to your spreadsheet columns in order (Column A,
              B, C...)
            </p>
            <ColumnList
              columns={visibleColumns}
              allColumns={columns}
              onChange={setColumns}
              onToggle={handleToggleVisibility}
              getLabel={getLabel}
            />
          </div>

          {hiddenColumns.length > 0 ? (
            <div>
              <h3 className="mb-1 text-sm font-medium text-gray-500">
                Hidden Columns
              </h3>
              <p className="mb-3 text-xs text-gray-500">
                These columns won&apos;t be mapped from your spreadsheet
              </p>
              <ul className="space-y-1">
                {hiddenColumns.map((col) => (
                  <li
                    key={col.id}
                    className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                  >
                    <span className="text-sm text-gray-400">
                      {getLabel(col.id)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleToggleVisibility(col.id)}
                      className="rounded p-1 text-gray-400 hover:bg-green-50 hover:text-green-600"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-6 flex justify-end">
            <Button onClick={onSave} disabled={isSaving}>
              Save
            </Button>
          </div>
        </div>
      );
    case "settings":
      return (
        <div className="space-y-8">
          <div>
            <h3 className="mb-4 text-sm font-medium text-gray-900">
              Paste Settings
            </h3>
            <Field>
              <Label>Date format</Label>
              <Select
                value={settings.date_format}
                onChange={(e) =>
                  setSettings((s) => ({
                    ...s,
                    date_format: e.target.value,
                  }))
                }
              >
                <optgroup label="Day/Month/Year (UK, AU, NZ)">
                  <option value="DD/MM/YYYY">
                    07/02/2024 (with leading zeros)
                  </option>
                  <option value="D/M/YYYY">7/2/2024 (no leading zeros)</option>
                </optgroup>
                <optgroup label="Month/Day/Year (US)">
                  <option value="MM/DD/YYYY">
                    02/07/2024 (with leading zeros)
                  </option>
                  <option value="M/D/YYYY">2/7/2024 (no leading zeros)</option>
                </optgroup>
                <optgroup label="ISO Format">
                  <option value="YYYY-MM-DD">2024-02-07</option>
                </optgroup>
              </Select>
            </Field>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="mb-4 text-sm font-medium text-gray-900">
              Spreadsheet Settings
            </h3>
            <div className="space-y-6">
              <SwitchField>
                <Label>Ignore Heading</Label>
                <Description>
                  Ignore the first line when importing a spreadsheet
                </Description>
                <Switch
                  checked={settings.ignore_header_row}
                  onChange={(checked) =>
                    setSettings((s) => ({
                      ...s,
                      ignore_header_row: checked,
                    }))
                  }
                />
              </SwitchField>

              <SwitchField>
                <Label>Ignore Missing Rego</Label>
                <Description>
                  Ignore any rows that are missing a line/rego no.
                </Description>
                <Switch
                  checked={settings.ignore_missing_line}
                  onChange={(checked) =>
                    setSettings((s) => ({
                      ...s,
                      ignore_missing_line: checked,
                    }))
                  }
                />
              </SwitchField>

              <Field>
                <Label>Ignore Columns</Label>
                <Description>
                  Ignore specific columns when importing from a spreadsheet
                </Description>
                <Input
                  placeholder="e.g. A,B,C"
                  value={settings.ignore_columns}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      ignore_columns: e.target.value,
                    }))
                  }
                />
              </Field>

              <SwitchField>
                <Label>Merge Duplicates</Label>
                <Description>
                  Merge rows that contain the same vehicle, origin, destination
                  and dates
                </Description>
                <Switch
                  checked={settings.merge_duplicates}
                  onChange={(checked) =>
                    setSettings((s) => ({
                      ...s,
                      merge_duplicates: checked,
                    }))
                  }
                />
              </SwitchField>

              <AdminSection>
                <SwitchField>
                  <Label>Ignore Fuel If Ferry</Label>
                  <Description>
                    If there is a fuel inclusion it will be ignored if a ferry
                    inclusion is present on the trip
                  </Description>
                  <Switch
                    checked={settings.ignore_fuel_if_has_ferry}
                    onChange={(checked) =>
                      setSettings((s) => ({
                        ...s,
                        ignore_fuel_if_has_ferry: checked,
                      }))
                    }
                  />
                </SwitchField>
              </AdminSection>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={onSave} disabled={isSaving}>
              Save
            </Button>
          </div>
        </div>
      );
    default:
      return null;
  }
}

function ColumnList({
  columns,
  allColumns,
  onChange,
  onToggle,
  getLabel,
}: {
  columns: ColumnItem[];
  allColumns: ColumnItem[];
  onChange: (columns: ColumnItem[]) => void;
  onToggle: (id: ImportColumnId) => void;
  getLabel: (id: ImportColumnId) => string;
}) {
  const [active, setActive] = useState<Active | null>(null);
  const activeItem = useMemo(
    () => columns.find((item) => item.id === active?.id),
    [active, columns],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={({ active }) => setActive(active)}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) {
          const activeIdx = allColumns.findIndex((c) => c.id === active.id);
          const overIdx = allColumns.findIndex((c) => c.id === over.id);
          onChange(arrayMove(allColumns, activeIdx, overIdx));
        }
        setActive(null);
      }}
      onDragCancel={() => setActive(null)}
    >
      <SortableContext items={columns} strategy={verticalListSortingStrategy}>
        <ul className="space-y-1">
          {columns.map((col, index) => (
            <ColumnRow
              key={col.id}
              column={col}
              index={index}
              label={getLabel(col.id)}
              onToggle={onToggle}
            />
          ))}
        </ul>
      </SortableContext>
      {createPortal(
        <SortableOverlay>
          {activeItem ? (
            <ColumnRowContent
              column={activeItem}
              index={columns.findIndex((c) => c.id === activeItem.id)}
              label={getLabel(activeItem.id)}
            />
          ) : null}
        </SortableOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

function ColumnRow({
  column,
  index,
  label,
  onToggle,
}: {
  column: ColumnItem;
  index: number;
  label: string;
  onToggle: (id: ImportColumnId) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2"
    >
      <button
        className="cursor-grab touch-none text-gray-400 hover:text-gray-600"
        {...attributes}
        {...listeners}
      >
        <Bars3Icon className="h-4 w-4" />
      </button>
      <span className="min-w-[2ch] text-xs font-medium text-gray-400">
        {columnIndexToLetter(index)}
      </span>
      <span className="flex-1 text-sm text-gray-900">{label}</span>
      <button
        type="button"
        onClick={() => onToggle(column.id)}
        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
      >
        <MinusIcon className="h-4 w-4" />
      </button>
    </li>
  );
}

function ColumnRowContent({
  index,
  label,
}: {
  column: ColumnItem;
  index: number;
  label: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg">
      <Bars3Icon className="h-4 w-4 text-gray-400" />
      <span className="min-w-[2ch] text-xs font-medium text-gray-400">
        {columnIndexToLetter(index)}
      </span>
      <span className="flex-1 text-sm text-gray-900">{label}</span>
    </div>
  );
}

function columnIndexToLetter(index: number): string {
  let result = "";
  let n = index;
  do {
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return result;
}
